/**
 * Proxy rule per hosting provider (AC-036) + shared code-block / copy primitives of the path-mode panel.
 * Pure UI: the snippet text comes from utils/proxySnippets.ts (approved mock), Copy copies exactly the shown text.
 * Tabs: `role=tablist/tab/tabpanel`, arrow keys switch providers (selection follows focus, AC-050).
 */
import React, { useState } from 'react';
import { Alert, Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { CUSTOM_DOMAIN_TEST_IDS } from '@modules/custom-domains/testIds';
import {
  PROXY_PROVIDERS,
  PROXY_PROVIDER_LABELS,
  buildProxySnippet,
  proxySnippetCaption,
  type ProxyProvider,
} from '@modules/custom-domains/utils/proxySnippets';
import { PATH_MODE_COPY } from '@modules/custom-domains/components/articlePathModeCopy';

const ids = CUSTOM_DOMAIN_TEST_IDS.baseUrl;

/** Writes `text` to the clipboard; a missing/blocked clipboard is ignored (the text stays selectable on screen). */
export function copyToClipboard(text: string): void {
  void navigator.clipboard?.writeText(text).catch(() => undefined);
}

export function CodeBlock({ children, testId }: { children: string; testId?: string }) {
  return (
    <Box
      component="pre"
      data-testid={testId}
      sx={{
        m: 0, p: 2, bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto',
        fontFamily: 'monospace', fontSize: '0.875rem', lineHeight: 1.6, color: 'text.primary',
      }}
    >
      {children}
    </Box>
  );
}

export function CopyButton({ text, label, testId }: { text: string; label: string; testId: string }) {
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<ContentCopyIcon />}
      aria-label={label}
      onClick={() => copyToClipboard(text)}
      data-testid={testId}
      sx={{ flexShrink: 0 }}
    >
      {PATH_MODE_COPY.buttons.copy}
    </Button>
  );
}

export interface ProxySnippetTabsProps {
  articleHost: string;
  /** A base URL the shared validator accepted (or the stored one). */
  publicBaseUrl: string;
}

const tabId = (provider: ProxyProvider) => `custom-domain-base-url-tab-el-${provider}`;
const panelId = (provider: ProxyProvider) => `custom-domain-base-url-tabpanel-${provider}`;

export function ProxySnippetTabs({ articleHost, publicBaseUrl }: ProxySnippetTabsProps) {
  const [provider, setProvider] = useState<ProxyProvider>('cloudflare');
  const input = { articleHost, publicBaseUrl };
  const { host, pathname } = new URL(publicBaseUrl);
  const snippet = buildProxySnippet(provider, input);
  const caption = proxySnippetCaption(provider, input);
  const label = PROXY_PROVIDER_LABELS[provider];

  return (
    <Box>
      <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
        {PATH_MODE_COPY.proxy.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {PATH_MODE_COPY.proxy.intro(host, pathname.replace(/\/$/, ''), articleHost)}
      </Typography>
      <Tabs
        value={provider}
        onChange={(_event, next: ProxyProvider) => setProvider(next)}
        variant="scrollable"
        allowScrollButtonsMobile
        selectionFollowsFocus
        aria-label={PATH_MODE_COPY.proxy.tabsLabel}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        {PROXY_PROVIDERS.map((p) => (
          <Tab
            key={p}
            value={p}
            label={PROXY_PROVIDER_LABELS[p]}
            id={tabId(p)}
            aria-controls={panelId(p)}
            data-testid={ids.snippetTab(p)}
          />
        ))}
      </Tabs>
      <Box role="tabpanel" id={panelId(provider)} aria-labelledby={tabId(provider)}>
        {caption === null ? (
          <Alert severity="info" data-testid={ids.snippetCode(provider)}>{snippet}</Alert>
        ) : (
          <>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', minWidth: 0, overflowWrap: 'anywhere' }}>
                {caption}
              </Typography>
              <CopyButton text={snippet} label={PATH_MODE_COPY.proxy.copyLabel(label)} testId={ids.snippetCopy(provider)} />
            </Stack>
            <CodeBlock testId={ids.snippetCode(provider)}>{snippet}</CodeBlock>
          </>
        )}
      </Box>
    </Box>
  );
}

export default ProxySnippetTabs;
